import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Instagram, Facebook, Mail, Loader2, Check } from 'lucide-react'
import { supabase } from '../../lib/supabase'

const footerLinks = {
  shop: [
    { name: 'All Products', href: '/shop' },
    { name: 'Teethers', href: '/shop/teethers' },
    { name: 'Gift Sets', href: '/shop/gift-sets' },
    { name: 'Accessories', href: '/shop/accessories' },
  ],
  company: [
    { name: 'About Us', href: '/about' },
    { name: 'The Science', href: '/science' },
    { name: 'Contact', href: '/contact' },
    { name: 'FAQ', href: '/faq' },
  ],
  support: [
    { name: 'Shipping & Returns', href: '/shipping' },
    { name: 'Privacy Policy', href: '/privacy' },
    { name: 'Terms of Service', href: '/terms' },
  ],
}

const socialLinks = [
  { name: 'Instagram', href: 'https://instagram.com/petitepalate', icon: Instagram, active: false },
  { name: 'Facebook', href: 'https://facebook.com/petitepalate', icon: Facebook, active: false },
  { name: 'Email', href: 'mailto:hello@petitepalate.com.au', icon: Mail, active: true },
]

function NewsletterForm() {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState('idle') // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!email) return

    setStatus('loading')
    setErrorMessage('')

    try {
      const { error } = await supabase
        .from('newsletter_subscribers')
        .insert({ email })

      if (error) {
        if (error.code === '23505') {
          setErrorMessage("You're already subscribed!")
          setStatus('error')
        } else {
          throw error
        }
      } else {
        setStatus('success')
        setEmail('')
      }
    } catch (error) {
      console.error('Newsletter signup error:', error)
      setErrorMessage('Something went wrong. Please try again.')
      setStatus('error')
    }
  }

  if (status === 'success') {
    return (
      <div className="flex items-center gap-2 text-pp-sage text-sm">
        <Check className="w-4 h-4" />
        <span>Thanks for subscribing!</span>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <div className="flex gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          required
          className="flex-1 px-3 py-2 text-sm rounded-lg border border-pp-sand bg-white focus:outline-none focus:ring-2 focus:ring-pp-terracotta/20 focus:border-pp-terracotta"
        />
        <button
          type="submit"
          disabled={status === 'loading'}
          className="px-4 py-2 bg-pp-terracotta text-white text-sm font-medium rounded-lg hover:bg-pp-terracotta/90 transition-colors disabled:opacity-50"
        >
          {status === 'loading' ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            'Subscribe'
          )}
        </button>
      </div>
      {status === 'error' && (
        <p className="text-red-500 text-xs">{errorMessage}</p>
      )}
    </form>
  )
}

export default function Footer() {
  return (
    <footer className="bg-pp-linen border-t border-pp-sand">
      {/* Newsletter Section */}
      <div className="border-b border-pp-sand">
        <div className="container mx-auto px-4 lg:px-8 py-10">
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="font-display text-xl text-pp-ink mb-2">
              Stay in the Loop
            </h3>
            <p className="text-pp-stone text-sm mb-4">
              Be the first to know about new products, exclusive offers, and parenting tips.
            </p>
            <div className="max-w-md mx-auto">
              <NewsletterForm />
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-12 lg:py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block">
              <span className="font-display text-2xl font-semibold text-pp-ink">
                Petite Palate
              </span>
            </Link>
            <p className="mt-4 text-pp-stone text-sm leading-relaxed max-w-sm">
              Real-food sensory teethers shaped around the foods your baby meets first.
              Crafted with love, backed by developmental science.
            </p>
            <div className="mt-6 flex gap-4">
              {socialLinks.map((item) =>
                item.active ? (
                  <a
                    key={item.name}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-full bg-pp-sand/50 text-pp-charcoal hover:bg-pp-terracotta hover:text-white transition-colors"
                    aria-label={item.name}
                  >
                    <item.icon className="w-5 h-5" />
                  </a>
                ) : (
                  <span
                    key={item.name}
                    className="p-2 rounded-full bg-pp-sand/50 text-pp-stone cursor-default"
                    aria-label={`${item.name} (coming soon)`}
                  >
                    <item.icon className="w-5 h-5" />
                  </span>
                )
              )}
            </div>
          </div>

          {/* Shop Links */}
          <div>
            <h4 className="font-display text-lg font-semibold text-pp-ink mb-4">
              Shop
            </h4>
            <ul className="space-y-3">
              {footerLinks.shop.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-pp-stone hover:text-pp-terracotta transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-display text-lg font-semibold text-pp-ink mb-4">
              Company
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-pp-stone hover:text-pp-terracotta transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h4 className="font-display text-lg font-semibold text-pp-ink mb-4">
              Support
            </h4>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-pp-stone hover:text-pp-terracotta transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-pp-sand flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-pp-stone">
            © {new Date().getFullYear()} Petite Palate. All rights reserved.
          </p>
          <p className="text-xs text-pp-stone">
            Made with care in Australia
          </p>
        </div>
      </div>
    </footer>
  )
}
